<template>
  <div>
    <b-row>
      <b-col
        class="d-none d-lg-block"
        cols="2"
      >
        <ProductFilter
          :constraints="searchFilter.constraints"
          :filter="searchFilter.filter"
          :filter-display="filterDisplay.data"
          :filter-display-loading="filterDisplay.loading"
          :is-autocall-tab-active="isAutocallTabActive"
          @clear-filter="clearFilters"
          @filter-change="updateFromFilter"
          @constraint-change="updateFromConstraint"
        >
          <template #hint>
            <hint-popup
              :id="29"
              class="ml-50"
            />
          </template>
        </ProductFilter>
      </b-col>
      <b-col
        cols="12"
        lg="10"
      >
        <div v-if="isLoggedIn">
          <recently-viewed-products
            compact
            is-current="true"
          />
        </div>

        <div class="d-flex justify-content-between align-items-center">
          <h2 class="mb-1">
            Current UK Structured Products
          </h2>
          <div class="d-flex align-items-center">
            <b-button
              variant="primary"
              class="btn-icon d-lg-none mr-1"
              @click="filterSidebar = !filterSidebar"
            >
              <feather-icon
                size="20"
                icon="FilterIcon"
              />
            </b-button>
            <b-button
              v-if="false"
              variant="primary"
              class="btn-icon rounded-circle"
              @click="filterHelpModal = true"
            >
              <feather-icon
                size="20"
                icon="HelpCircleIcon"
              />
            </b-button>
          </div>
        </div>
        <div class="my-0 mb-1 d-flex align-items-center">
          <h5 class="mb-0">
            {{ resultsText }}
          </h5>
          <b-badge
            v-if="recentProductCount > 0"
            variant="light-success"
            pill
            class="ml-1 current-products-recent-badge"
          >
            {{ recentProductCount }} products added in last week
          </b-badge>
          <b-button
            v-if="isPremium"
            variant="outline-secondary"
            size="sm"
            class="ml-auto flex-shrink-0 d-flex align-items-center"
            :disabled="csvDownloading || products.loading || products.error || !products.data.length"
            @click="downloadCsv"
          >
            <b-img
              :src="require('@/assets/images/icons/file-icons/csv.png')"
              height="20"
              width="20"
              alt=""
            />
            <span class="ml-50 font-weight-bold text-body">{{ csvDownloading ? 'Downloading...' : 'Download CSV' }}</span>
          </b-button>
        </div>
        <div class="ecommerce-searchbar mt-1 mb-2">
          <b-input-group class="input-group-merge">
            <b-form-input
              v-model="searchInput"
              placeholder="Search (ID, name or identifier)"
              aria-label="Search current products by ID, name or identifier"
              class="search-product"
              @change="applySearch"
              @keyup.enter="applySearch"
            />
            <b-input-group-append is-text>
              <button
                v-if="searchInput || searchFilter.search"
                type="button"
                class="p-0 mr-50 border-0 bg-transparent text-muted"
                aria-label="Clear product search"
                @click="clearSearch"
              >
                <feather-icon icon="XIcon" />
              </button>
              <button
                type="button"
                class="p-0 border-0 bg-transparent text-muted"
                aria-label="Search current products"
                @click="applySearch"
              >
                <feather-icon
                  icon="SearchIcon"
                  class="text-muted"
                />
              </button>
            </b-input-group-append>
          </b-input-group>
        </div>
        <b-alert
          :show="products.error"
          variant="danger"
        >
          Products could not be loaded. Please try searching again.
        </b-alert>
        <b-tabs v-model="activeTabIndex" nav-class="wider-tabs" pills cards>
          <b-tab active>
            <template #title>
              <feather-icon
                icon="GridIcon"
                size="16"
                class="mr-50"
              />
              <span>Grid</span>
            </template>
            <p class="mb-1">Sortable table of matched products with key details listed</p>
            <current-products-grid
              :products="products"
              :search="searchFilter.search"
              :filter-data="searchFilter.filter"
              :filter-display="filterDisplay.data"
              :constraints="searchFilter.constraints"
              :filter-interaction-order="filterInteractionOrder"
              :filter-sequential-counts="filterSequentialCounts"
              :total-product-count="totalProductCount"
              @portfolio-updated="updateData"
            />
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon
                icon="ShuffleIcon"
                size="16"
                class="mr-50"
              />
              <span>Autocall</span>
            </template>
            <p class="mb-1">Autocall-specific view showing barrier levels and observation schedules</p>
            <div v-if="!isPremium">
              <UpgradeToPremium />
            </div>
            <div v-else>
              <current-products-autocall
                :autocall-data="autocallContent"
              />
            </div>
          </b-tab>

          <b-tab @click="onVisualTabActivate">
            <template #title>
              <feather-icon
                icon="EyeIcon"
                size="16"
                class="mr-50"
              />
              <span>Visual</span>
            </template>
            <p class="mb-1">Headline yield values plotted by maturity length or yield to give a visual indication of how headline yields are distributed. See report risk and scoring to understand full product characteristics</p>
            <div v-if="!isPremium">
              <UpgradeToPremium />
            </div>
            <div v-else>
              <current-products-visual
                ref="visualChart"
                :visual-content="visualContent"
                :loading="products.loading"
                :visual-settings="visualSettings"
                :x-axis-options="xAxisOptions"
                :maturity-mode-options="maturityModeOptions"
                @update-visual-settings="updateVisualData"
              />
            </div>
          </b-tab>

          <b-tab @click="onScatterTabActivate">
            <template #title>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                class="mr-50"
                style="display: inline-block; vertical-align: middle;"
              >
                <path
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 3v18h18M8 15.015v.015m8 .985v.015m-8-9v.015m4 3.985v.015m7-.015v.015"
                />
              </svg>
              <span>Scatter</span>
            </template>
            <p class="mb-1">Scatter chart showing products by risk-score, or by category pairs grouped by product type, provider or counterparty</p>
            <div v-if="!isPremium">
              <UpgradeToPremium />
            </div>
            <div v-else>
              <current-products-scatter
                ref="scatterChart"
                :scatter-content="scatterContent"
                :loading="products.loading"
                :scatter-settings="scatterSettings"
                :axis-mode-options="axisModeOptions"
                :grouping-mode-options="groupingModeOptions"
                :category-options="categoryOptions"
                @update-scatter-settings="updateScatterData"
              />
            </div>
          </b-tab>

        </b-tabs>
      </b-col>
    </b-row>

    <b-sidebar
      v-model="filterSidebar"
      :backdrop="true"
      :no-header="true"
      :body-class="filterSidebarClass"
    >
      <div class="p-2">
        <ProductFilter
          :constraints="searchFilter.constraints"
          :filter="searchFilter.filter"
          :filter-display="filterDisplay.data"
          :is-autocall-tab-active="isAutocallTabActive"
          @clear-filter="clearFilters"
          @filter-change="updateFromFilter"
          @constraint-change="updateFromConstraint"
        >
          <template #hint>
            <hint-popup
              :id="29"
              class="ml-50"
            />
          </template>
        </ProductFilter>
      </div>
    </b-sidebar>

    <b-modal
      v-model="filterHelpModal"
      centered
      size="lg"
      title="Product filter help"
      ok-only
      ok-title="Ok"
    >
      <div>
        <p>
      The filter allows you to find structured products on this service. Select one or more options on the column on the left hand side of this page by checking the appropriate entries to narrow your search. The right hand side of the page will update automatically as you make your selections.
        </p>

        <p>
   The head-to-head lets you compare the features of between two and four products side by side. Click the button then select products from the filter results by clicking on each chosen row. This is only available on the premium version.
        </p>

        <p>
       The selection record requires you to choose one or more products from the filter results. This powerful feature will allow you to save your particular product search to evidence your product selection process. The PDF report that is generated is saved and can be downloaded from the “Your usage” page on your account . This is only available on the premium version.   </p>

      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BModal, BSidebar, BTabs, BTab, BBadge,
  BFormInput, BInputGroup, BInputGroupAppend, BAlert, BImg,
} from 'bootstrap-vue'
import axios from 'axios'
import { mapGetters } from 'vuex'
import ProductFilter from '@/views/components/ProductFilter.vue'
import HintPopup from '@/views/components/HintPopup.vue'
import UpgradeToPremium from '@/views/components/UpgradeToPremium.vue'
import CurrentProductsGrid from './current/CurrentProductsGrid.vue'
import CurrentProductsVisual from './current/CurrentProductsVisual.vue'
import CurrentProductsScatter from './current/CurrentProductsScatter.vue'
import CurrentProductsAutocall from './current/CurrentProductsAutocall.vue'
import RecentlyViewedProducts from './current/RecentlyViewedProducts.vue'

export default {
  components: {
    ProductFilter,
    HintPopup,
    BRow,
    BCol,
    BButton,
    BModal,
    BSidebar,
    BTabs,
    BTab,
    BBadge,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BAlert,
    BImg,
    CurrentProductsGrid,
    CurrentProductsVisual,
    CurrentProductsScatter,
    CurrentProductsAutocall,
    RecentlyViewedProducts,
    UpgradeToPremium,
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      filterSidebar: false,
      activeTabIndex: 0,
      searchInput: '',
      csvDownloading: false,
      requestVersions: { products: 0, filters: 0, counts: 0 },
      cancelTokens: {},
      searchFilter: {
        search: '',
        filter: {},
        constraints: {
          dateFrom: '',
          dateTo: '',
          dateType: 'None',
        },
      },
      filterSelections: {},
      filterInteractionOrder: [],
      filterSequentialCounts: {}, // categoryKey -> product count after applying filters up to and including that key
      totalProductCount: 0,
      recentProductCount: 0,
      filterDisplay: {
        data: [],
        loading: true,
        error: false,
      },
      filterHelpModal: false,
      products: {
        loading: true,
        error: false,
        data: [],
      },
      autocallContent: {
        loading: true,
        error: false,
        data: [],
      },
      runOnMount: undefined,
      visualContent: null,
      visualSettings: {
        xAxisMode: 'maturity',
        maturityMode: 'first',
      },
      scatterContent: null,
      scatterSettings: {
        axisMode: 'riskmap-score',
        groupingMode: 'Product Type',
        category1: 'capitalscore',
        category2: 'aimscore',
      },
      categoryOptions: [],
      xAxisOptions: [
        { text: 'By Maturity', value: 'maturity' },
        { text: 'By Yield', value: 'yield' },
      ],
      maturityModeOptions: [
        { text: 'First', value: 'first' },
        { text: 'Final', value: 'final' },
      ],
      axisModeOptions: [
        { text: 'Riskmap-Overall Score', value: 'riskmap-score' },
        { text: 'Categories', value: 'categories' },
      ],
      groupingModeOptions: [
        { text: 'Product Type', value: 'Product Type' },
        { text: 'Provider', value: 'Provider' },
        { text: 'Counterparty', value: 'Counterparty' },
      ],
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
      'isPremium',
    ]),
    filterSidebarClass() {
      return this.$store.state.appConfig.layout.skin === 'dark' ? 'filter-sidebar-dark-bg custom-scrollbar' : 'filter-sidebar-light-bg custom-scrollbar'
    },
    isAutocallTabActive() {
      // Tab index 1 is the Autocall tab (0=Grid, 1=Autocall, 2=Visual, 3=Scatter)
      return this.activeTabIndex === 1
    },
    resultsText() {
      if (this.products.loading) return 'Loading results...'
      if (this.products.error) return 'Results unavailable'
      if (this.isAutocallTabActive) {
        const autocallCount = this.autocallContent.data.length
        const totalCount = this.products.data.length
        return `${autocallCount} autocalls from ${totalCount} results`
      }
      return `${this.products.data.length} results`
    },
  },
  watch: {
    async isLoggedIn() {
      await Promise.all([this.loadFilters(), this.loadCategoryOptions()])
      this.refreshResults()
    },
  },
  async activated() {
    if (this.$route.params.category) {
      this.filterSelections = { [this.$route.params.category]: [String(this.$route.params.value)] }
      this.updateFromFilter(this.filterSelections)
    }
  },
  async created() {
    this.loadProductCount()
    this.loadTotalProductCount()
    if (this.$route.params.category) {
      this.filterSelections = { [this.$route.params.category]: [String(this.$route.params.value)] }
      this.searchFilter.filter = this.filterSelections
    }
    await Promise.all([this.loadFilters(), this.loadCategoryOptions()])
    this.refreshResults()
  },
  beforeDestroy() {
    Object.keys(this.requestVersions).forEach(key => {
      this.requestVersions[key] += 1
      if (this.cancelTokens[key]) this.cancelTokens[key].cancel()
    })
  },
  methods: {
    async downloadCsv() {
      if (!this.isPremium || this.csvDownloading || this.products.loading || this.products.error || !this.products.data.length) return
      this.csvDownloading = true
      try {
        const response = await this.$http.get('productfilter.cfc?method=downloadCSVCurrentProducts', {
          params: { searchfilter: JSON.stringify(this.searchSnapshot()) },
          responseType: 'blob',
        })
        const disposition = response.headers['content-disposition'] || ''
        const filename = disposition.match(/filename="([^"]+)"/i)
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        try {
          link.style.display = 'none'
          link.href = url
          link.download = filename ? filename[1] : 'current_products.csv'
          document.body.appendChild(link)
          link.click()
        } finally {
          link.remove()
          window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
        }
      } catch (error) {
        this.$bvToast.toast('CSV download failed. Please try again.', {
          title: 'Download failed',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.csvDownloading = false
      }
    },
    beginRequest(key) {
      if (this.cancelTokens[key]) this.cancelTokens[key].cancel()
      this.cancelTokens[key] = axios.CancelToken.source()
      this.requestVersions[key] += 1
      return { version: this.requestVersions[key], token: this.cancelTokens[key].token }
    },
    searchSnapshot() {
      return JSON.parse(JSON.stringify(this.searchFilter))
    },
    applySearch() {
      const search = this.searchInput.trim()
      this.searchInput = search
      if (search === this.searchFilter.search && !this.products.error) return
      this.searchFilter.search = search
      this.refreshResults()
    },
    clearSearch() {
      this.searchInput = ''
      this.applySearch()
    },
    clearFilters() {
      this.searchInput = ''
      this.searchFilter = {
        search: '',
        filter: {},
        constraints: { dateType: 'None', dateTo: '', dateFrom: '' },
      }
      this.filterSelections = {}
      this.filterInteractionOrder = []
      // ProductFilter emits filter-change after resetting its sliders.
    },
    refreshResults() {
      this.updateFilters()
      this.updateData()
      this.updateSequentialCounts(this.searchFilter.filter)
    },
    loadTotalProductCount() {
      return this.$http.get('productfilter.cfc?method=getFilterResults', {
        params: { searchFilter: { filter: {} }, countsOnly: true },
      }).then(response => {
        this.totalProductCount = response.data.count
      }).catch(() => {})
    },
    loadProductCount() {
      return this.$http
        .get('public.cfc?method=getProductCount')
        .then(response => {
          this.recentProductCount = response.data.RECENT || 0
        })
        .catch(() => {
          this.recentProductCount = 0
        })
    },
    loadFilters(toggleLoading = true) {
      this.filterDisplay.loading = true
      this.filterDisplay.error = false
      return this.$http
        .get('productFilter.cfc?method=getFilterDisplay', {
          params: {
            filterStatus: 0,
          },
        })
        .then(response => {
          this.filterDisplay.data = response.data.content
          // Index the filter data so that items can be looked up by value
          this.filterDisplay.data.forEach(category => {
            // eslint-disable-next-line no-param-reassign
            category.valueIndex = {}
            category.items.forEach((item, index) => {
              // eslint-disable-next-line no-param-reassign
              category.valueIndex[item.value] = index
            })
          })
        })
        .catch(() => {
          this.filterDisplay.error = true
        })
        .then(() => {
          if (toggleLoading) {
            this.filterDisplay.loading = false
          }
        })
    },
    loadCategoryOptions() {
      return this.$http
        .get('productFilter.cfc?method=getCategoryOptions')
        .then(response => {
          this.categoryOptions = response.data
        })
        .catch(error => {
          console.error('ERROR in loadCategoryOptions:', error)
        })
    },
    updateFilters() {
      const request = this.beginRequest('filters')
      this.filterDisplay.error = false

      return this.$http
        .get('productfilter.cfc?method=getFilterDisplay', {
          params: {
            searchFilter: this.searchSnapshot(),
            filterStatus: 0,
          },
          cancelToken: request.token,
        })
        .then(response => {
          if (request.version !== this.requestVersions.filters) return
          // Build a lookup table of non-slider response categories by categoryKey
          const responseMap = {}
          response.data.content.forEach(cat => {
            if (!cat.slider) {
              responseMap[cat.categoryKey] = cat
            }
          })

          // Merge into local filterDisplay
          this.filterDisplay.data.forEach(displayCategory => {
            // Skip sliders entirely
            if (displayCategory.slider) return

            const key = displayCategory.categoryKey
            const responseCategory = responseMap[key]

            // --- Reset all counts to 0 ---
            displayCategory.items.forEach(item => {
              const updatedItem = item
              updatedItem.count = 0
            })

            // --- Merge updated counts, if response category exists ---
            if (responseCategory) {
              const updatedDisplayCategory = displayCategory
              responseCategory.items.forEach(respItem => {
                const idx = updatedDisplayCategory.valueIndex[respItem.value]
                if (idx !== undefined) {
                  updatedDisplayCategory.items[idx].count = respItem.count
                }
              })
            }
          })
          this.filterDisplay.loading = false
        })
        .catch(error => {
          if (request.version !== this.requestVersions.filters || axios.isCancel(error)) return
          console.error('ERROR in updateFilters:', error)
          this.filterDisplay.error = true
          this.filterDisplay.loading = false
        })
    },
    updateFromFilter(newFilter, changedKey) {
      if (changedKey) {
        const idx = this.filterInteractionOrder.indexOf(changedKey)
        if (idx > -1) this.filterInteractionOrder.splice(idx, 1)
        this.filterInteractionOrder.push(changedKey)
      }
      this.searchFilter.filter = newFilter
      this.filterSelections = newFilter
      this.refreshResults()
    },
    updateSequentialCounts(filterData) {
      const request = this.beginRequest('counts')
      const searchFilter = this.searchSnapshot()
      this.filterSequentialCounts = {}
      // Get active filter keys in interaction order, then any remaining keys
      const activeKeys = Object.keys(filterData).filter(k => filterData[k] && filterData[k].length > 0)
      if (activeKeys.length === 0 && !searchFilter.search) {
        return Promise.resolve()
      }

      // Order keys: interacted keys in order, then any others
      const ordered = this.filterInteractionOrder.filter(k => activeKeys.includes(k))
      const unordered = activeKeys.filter(k => !ordered.includes(k))
      const orderedKeys = [...unordered, ...ordered]

      // Fire one API call per key with progressively-built filter
      const builtFilter = {}
      const promises = orderedKeys.map(key => {
        builtFilter[key] = filterData[key]
        const filterSnapshot = { ...builtFilter }
        return this.$http.get('productfilter.cfc?method=getFilterResults', {
          params: {
            searchFilter: { ...searchFilter, filter: filterSnapshot },
            countsOnly: true,
          },
          cancelToken: request.token,
        }).then(response => ({
          key,
          count: response.data.count,
        }))
      })

      if (searchFilter.search) {
        promises.unshift(this.$http.get('productfilter.cfc?method=getFilterResults', {
          params: {
            searchFilter: { search: searchFilter.search, filter: {} },
            countsOnly: true,
          },
          cancelToken: request.token,
        }).then(response => ({ key: 'search', count: response.data.count })))
      }

      return Promise.all(promises).then(results => {
        if (request.version !== this.requestVersions.counts) return
        const counts = {}
        results.forEach(r => { counts[r.key] = r.count })
        this.filterSequentialCounts = counts
      }).catch(() => {})
    },
    updateFromConstraint(newConstraints) {
      this.searchFilter.constraints = newConstraints
      this.refreshResults()
    },
    updateData() {
      const request = this.beginRequest('products')
      this.products.data = []
      this.products.loading = true
      this.products.error = false
      this.autocallContent.data = []
      this.autocallContent.loading = true
      this.autocallContent.error = false
      this.visualContent = null
      this.scatterContent = null

      const params = {
        searchFilter: this.searchSnapshot(),
        maturitymode: this.visualSettings.maturityMode,
        xaxismode: this.visualSettings.xAxisMode,
        axismode: this.scatterSettings.axisMode,
        displaymode: this.scatterSettings.groupingMode,
        category1: this.scatterSettings.category1,
        category2: this.scatterSettings.category2,
      }
      return this.$http
        .get('productfilter.cfc?method=getFilterResults', {
          params,
          cancelToken: request.token,
        })
        .then(response => {
          if (request.version !== this.requestVersions.products) return
          this.products.data = response.data.gridcontent
          this.autocallContent.data = response.data.autocallcontent
          this.visualContent = response.data.visualcontent
          this.scatterContent = response.data.scattercontent
        })
        .catch(error => {
          if (request.version !== this.requestVersions.products || axios.isCancel(error)) return
          this.products.error = true
          this.autocallContent.error = true
          console.error('ERROR in loadData:', error)
        })
        .then(() => {
          if (request.version !== this.requestVersions.products) return
          this.products.loading = false
          this.autocallContent.loading = false
        })
    },
    onVisualTabActivate() {
      // Resize chart when Visual tab becomes active
      this.$nextTick(() => {
        if (this.$refs.visualChart) {
          this.$refs.visualChart.resizeChart()
        }
      })
    },
    onScatterTabActivate() {
      // Resize chart when Scatter tab becomes active
      this.$nextTick(() => {
        if (this.$refs.scatterChart) {
          this.$refs.scatterChart.resizeChart()
        }
      })
    },
    updateVisualData(payload) {
      // Update the setting
      if (payload && payload.key && payload.value !== undefined) {
        this.visualSettings[payload.key] = payload.value
      }
      // Reload data with new visual settings
      this.updateData()
    },
    updateScatterData(payload) {
      // Update the setting
      if (payload && payload.key && payload.value !== undefined) {
        this.scatterSettings[payload.key] = payload.value
      }
      // Reload data with new scatter settings
      this.updateData()
    },
  },
}
</script>

<style>
/* Remove gap between tabs and content */
.nav-tabs {
  margin-bottom: 0 !important;
}

/* Make tabs wider */
.wider-tabs .nav-link {
  padding-left: 2rem !important;
  padding-right: 2rem !important;
}

/* Hover effect for pill tabs */
.wider-tabs .nav-link:not(.active):hover {
  background-color: rgba(26, 115, 232, 0.12);
  transition: all 0.2s ease;
}

.wider-tabs .nav-link {
  transition: all 0.2s ease;
}

/* Dark mode hover effect */
.dark-layout .wider-tabs .nav-link:not(.active):hover {
  background-color: rgba(26, 115, 232, 0.2);
}

.current-products-recent-badge {
  white-space: nowrap;
}
</style>
